import { z } from 'zod';
import { Console } from '@utils';
import { NEXT_PUBLIC_AMPLITUDE_API_KEY } from '@config/environment';
import { usePersistState } from './usePersistState';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { EventTrackingSchemas } from 'src/validators/Events';
import { ScreenEventType } from 'src/validators/Events/Screen';
import { useOrderState } from '@hooks/useOrderState';
import { useAuth } from './useAuth';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'next/router';
import { usePermissions } from './usePermissions';
import { usePostHog } from 'posthog-js/react';
import { flattenObject } from '@utils/collection';
import { User } from '@interfaces';

interface Result {
  event: Event;
  code: number;
  message: string;
}

interface AmplitudeReturn<T> {
  promise: Promise<T>;
}

interface AmplitudeIdentify {
  set(key: string, value: any): AmplitudeIdentify;
}

interface AmplitudeElement {
  track: (
    eventInput: keyof typeof EventTrackingSchemas,
    eventProps?: z.infer<
      typeof EventTrackingSchemas[keyof typeof EventTrackingSchemas]
    >,
  ) => AmplitudeReturn<Result>;
  Identify: new () => AmplitudeIdentify;
  identify: (userProps: AmplitudeIdentify) => void;
  setUserId: (value: string) => void;
}

declare global {
  interface Window {
    amplitude: AmplitudeElement;
  }
}

interface ScreenView {
  event: string;
  screenInstanceId?: string;
  screenType?: ScreenEventType;
}

export function useEventTracking() {
  const { appUser } = useAuth();
  const posthog = usePostHog();
  const [userId, setUserId, clearUserId] = usePersistState('ukey', '');
  const { orderUuid } = useOrderState();
  const isBrowser = typeof window !== 'undefined';
  const { userCan } = usePermissions();

  const router = useRouter();
  const routeKey = `screen_instance_id_${router.pathname}`;
  const [screenInstanceId, setScreenInstanceId] = usePersistState(routeKey, '');

  const lastScreenViewTime = useRef<number>(0);
  const lastScreenView = useRef<ScreenView | null>(null);

  const BASE_PROPS = useCallback(
    () => ({
      meki_session_id: '-',
      user_id: undefined,
      user_uuid: undefined,
      timestamp: new Date().toISOString(),
      order_uuid: orderUuid,
      screen_instance_id: screenInstanceId,
    }),
    [orderUuid, screenInstanceId],
  );

  const handleScreenViewEvent = useCallback(
    (event: string, screenInstanceId?: string) => {
      const now = Date.now();
      const timeSinceLastView = now - lastScreenViewTime.current;
      const isSameScreenAsBefore =
        lastScreenView.current?.event === event &&
        lastScreenView.current?.screenInstanceId === screenInstanceId;

      if (
        timeSinceLastView < 1000 ||
        isSameScreenAsBefore ||
        screenInstanceId == ''
      ) {
        return false;
      }

      lastScreenViewTime.current = now;
      lastScreenView.current = {
        event,
        screenInstanceId,
      };

      if (timeSinceLastView < 1000 || isSameScreenAsBefore) {
        return false;
      }
      return true;
    },
    [],
  );

  const track = useCallback(
    async <T extends keyof typeof EventTrackingSchemas>(
      event: T,
      eventProps?: z.infer<typeof EventTrackingSchemas[T]>,
      user?: User,
    ) => {
      if (!isBrowser || userCan('MANAGE_USER')) return;

      try {
        const userData = user
          ? user.segmentationInfo
          : appUser?.segmentationInfo;

        const basePropsWithSegmentationsTmp = {
          ...BASE_PROPS(),
          user_id: appUser?.id ?? undefined,
          user_uuid: appUser?.uuid ?? undefined,
          screen_instance_id: screenInstanceId,
          user_segmentation_data: {
            total_money_disbursed: userData?.totalMoneyDisbursed,
            total_orders_paid: appUser?.segmentationInfo?.totalOrdersPaid,
            days_since_last_order: userData?.daysSinceLastOrder,
          },
        };
        const basePropsWithSegmentations = flattenObject(
          basePropsWithSegmentationsTmp,
        );

        const isScreenViewEvent =
          event?.toString().includes('screen') &&
          event?.toString().includes('view');

        if (isScreenViewEvent) {
          const shouldSendViewEvent = handleScreenViewEvent(
            event.toString(),
            screenInstanceId,
          );
          if (!shouldSendViewEvent) return;
        }

        Console.log('[AMPLITUDE][TRACK]', {
          event,
          baseProps: basePropsWithSegmentations,
          eventProps,
          sendAmplitude: !!NEXT_PUBLIC_AMPLITUDE_API_KEY,
          windowAmplitud: window.amplitude,
        });

        posthog?.capture(event, {
          ...basePropsWithSegmentations,
          ...eventProps,
        });

        if (NEXT_PUBLIC_AMPLITUDE_API_KEY && window.amplitude) {
          const schema = EventTrackingSchemas[event];
          if (schema && eventProps) {
            schema.parse({
              ...basePropsWithSegmentations,
              ...eventProps,
            });
          }
          window.amplitude.track(event, {
            ...basePropsWithSegmentations,
            ...eventProps,
          });
        }
      } catch (error) {
        Console.error('[AMPLITUDE][TRACK] Error:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isBrowser, BASE_PROPS, handleScreenViewEvent],
  );

  const identify = useCallback(
    (newUserId: string, userProps) => {
      posthog?.identify(newUserId, userProps);
      if (!isBrowser || !window.amplitude) return;
      try {
        const identifyObj = new window.amplitude.Identify();
        Object.entries(userProps).forEach(([key, value]) => {
          identifyObj.set(key, value);
        });
        Console.log('[AMPLITUDE][IDENTIFY]', {
          sendAmplitude: !!NEXT_PUBLIC_AMPLITUDE_API_KEY,
          windowAmplitud: window.amplitude,
          identifyObj,
        });
        setUserId(newUserId);
        window.amplitude.setUserId(newUserId);
        window.amplitude.identify(identifyObj);
      } catch (error) {
        Console.error('[AMPLITUDE][IDENTIFY] Error:', error);
      }
    },
    [isBrowser],
  );

  useEffect(() => {
    const updateScreenInstanceOnRouteChange = () => {
      if (!screenInstanceId) {
        setScreenInstanceId(uuidv4());
      }
    };
    updateScreenInstanceOnRouteChange();
  }, [screenInstanceId, setScreenInstanceId]);

  const functions = useMemo(() => {
    return { track, identify };
  }, [track, identify]);

  return functions;
}
